import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
} from "recharts";
import { BarChart3, Donut, TrendingUp } from "lucide-react";

const SERIES_COLORS = ["#38bdf8", "#818cf8", "#34d399", "#f59e0b", "#fb7185", "#a78bfa", "#2dd4bf"];

const EmptyChartState = ({ icon: Icon, title, description }) => (
  <div className="flex h-[280px] flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-slate-700 bg-slate-950/40 text-center text-slate-400">
    <div className="rounded-2xl bg-slate-900 p-3 text-slate-300">
      <Icon size={20} />
    </div>
    <div>
      <p className="text-sm font-semibold text-slate-200">{title}</p>
      <p className="mt-1 max-w-xs text-xs leading-5 text-slate-500">{description}</p>
    </div>
  </div>
);

const ChartCard = ({ title, subtitle, icon: Icon, children }) => (
  <section className="rounded-3xl border border-slate-800 bg-slate-950/80 p-5 shadow-[0_24px_80px_rgba(15,23,42,0.28)]">
    <div className="mb-4 flex items-start justify-between gap-3">
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-[0.24em] text-slate-400">{title}</h3>
        <p className="mt-2 text-sm text-slate-500">{subtitle}</p>
      </div>
      <div className="rounded-2xl border border-slate-800 bg-slate-900/80 p-3 text-slate-200">
        <Icon size={18} />
      </div>
    </div>
    {children}
  </section>
);

export default function MatchesCharts({
  dateWisePublished = [],
  compatibilityBreakdown = [],
  sourceServiceBreakdown = [],
}) {
  const compatibilityData = useMemo(
    () => compatibilityBreakdown.filter((item) => Number(item?.value || 0) > 0),
    [compatibilityBreakdown],
  );
  const sourceData = useMemo(
    () => sourceServiceBreakdown.filter((item) => Number(item?.value || 0) > 0),
    [sourceServiceBreakdown],
  );
  const dateData = useMemo(
    () => dateWisePublished.filter((item) => Number(item?.value || 0) >= 0),
    [dateWisePublished],
  );

  return (
    <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
      <ChartCard
        title="Publishing Trend"
        subtitle="Published match counts grouped by the selected summary interval."
        icon={TrendingUp}
      >
        {dateData.length > 0 ? (
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={dateData} margin={{ top: 12, right: 12, left: -20, bottom: 4 }}>
                <CartesianGrid stroke="rgba(148, 163, 184, 0.12)" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip
                  cursor={{ stroke: "#38bdf8", strokeOpacity: 0.25 }}
                  contentStyle={{
                    background: "#020617",
                    border: "1px solid rgba(51, 65, 85, 0.9)",
                    borderRadius: 16,
                    color: "#e2e8f0",
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#38bdf8"
                  strokeWidth={3}
                  dot={{ fill: "#38bdf8", r: 4, strokeWidth: 0 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <EmptyChartState
            icon={TrendingUp}
            title="No trend data yet"
            description="Date-wise publishing counts will appear here after the summary API returns grouped results."
          />
        )}
      </ChartCard>

      <ChartCard
        title="Compatibility Mix"
        subtitle="Breakdown of published matches by compatibility level."
        icon={Donut}
      >
        {compatibilityData.length > 0 ? (
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={compatibilityData} dataKey="value" nameKey="name" innerRadius={68} outerRadius={94} paddingAngle={3}>
                  {compatibilityData.map((entry, index) => (
                    <Cell key={`${entry.name}-${index}`} fill={SERIES_COLORS[index % SERIES_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    background: "#020617",
                    border: "1px solid rgba(51, 65, 85, 0.9)",
                    borderRadius: 16,
                    color: "#e2e8f0",
                  }}
                />
                <Legend
                  wrapperStyle={{ fontSize: 12, color: "#cbd5e1" }}
                  formatter={(value) => <span className="text-slate-300">{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <EmptyChartState
            icon={Donut}
            title="No compatibility breakdown"
            description="Once published matches are available, this chart will show how they distribute across compatibility levels."
          />
        )}
      </ChartCard>

      <ChartCard
        title="Source Services"
        subtitle="Where the currently filtered published matches are coming from."
        icon={BarChart3}
      >
        {sourceData.length > 0 ? (
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={sourceData} margin={{ top: 12, right: 12, left: -12, bottom: 8 }}>
                <CartesianGrid stroke="rgba(148, 163, 184, 0.12)" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{
                    background: "#020617",
                    border: "1px solid rgba(51, 65, 85, 0.9)",
                    borderRadius: 16,
                    color: "#e2e8f0",
                  }}
                />
                <Bar dataKey="value" radius={[10, 10, 0, 0]}>
                  {sourceData.map((entry, index) => (
                    <Cell key={`${entry.name}-${index}`} fill={SERIES_COLORS[index % SERIES_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <EmptyChartState
            icon={BarChart3}
            title="No source data yet"
            description="Source service counts will appear here when the summary API returns filtered publishing data."
          />
        )}
      </ChartCard>
    </div>
  );
}
